import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { BookOpen, Eye, EyeOff, CheckCircle } from 'lucide-react';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError('Las contrasenas no coinciden'); return; }
    if (password.length < 6) { setError('La contrasena debe tener al menos 6 caracteres'); return; }
    setLoading(true);
    try {
      await authAPI.resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al restablecer. El enlace puede haber expirado.');
    }
    setLoading(false);
  };

  if (!token) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4 py-16">
        <div className="text-center">
          <p className="text-sm font-sans text-gray-500">Enlace invalido. Solicita un nuevo enlace de recuperacion.</p>
          <Link to="/recuperar-contrasena" className="text-sm font-sans text-trans-blue hover:underline font-medium mt-2 inline-block">Recuperar contrasena</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-sm animate-fade-up">
        <div className="text-center mb-8">
          <div className="w-10 h-10 rounded-sm bg-slate-dark flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-5 h-5 text-white" strokeWidth={1.5} />
          </div>
          <h1 className="text-2xl font-serif font-bold text-slate-dark">Nueva Contrasena</h1>
          <p className="text-sm font-sans text-gray-400 mt-1">Ingresa tu nueva contrasena</p>
        </div>

        {done ? (
          <div className="text-center space-y-4" data-testid="reset-success">
            <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mx-auto">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <p className="text-sm font-sans text-gray-600">Tu contrasena ha sido restablecida exitosamente.</p>
            <Button asChild className="bg-slate-dark font-sans mt-2">
              <Link to="/login">Iniciar Sesion</Link>
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-form">
            {error && <div className="p-3 text-sm font-sans bg-red-50 text-red-600 rounded-md border border-red-100">{error}</div>}
            <div className="space-y-1.5">
              <Label htmlFor="password" className="font-sans text-sm">Nueva contrasena</Label>
              <div className="relative">
                <Input id="password" type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required placeholder="Minimo 6 caracteres" data-testid="reset-password" className="font-sans pr-10" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm" className="font-sans text-sm">Confirmar contrasena</Label>
              <Input id="confirm" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required placeholder="Repite la contrasena" data-testid="reset-confirm" className="font-sans" />
            </div>
            <Button type="submit" className="w-full bg-slate-dark hover:bg-slate-dark/90 font-sans" disabled={loading} data-testid="reset-submit-btn">
              {loading ? 'Restableciendo...' : 'Restablecer Contrasena'}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
